import manifest from './demo_project.json';
import type { StickerCode } from '../sticker_types';

export interface DemoProjectCode {
  code: StickerCode;
  tag: string;
}

export interface DemoProject {
  projectName: string;
  tags: readonly string[];
  codes: readonly DemoProjectCode[];
}

function validateManifest(value: unknown): DemoProject {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Demo project manifest must be an object.');
  }

  const candidate = value as {
    projectName?: unknown;
    tags?: unknown;
    codes?: unknown;
  };
  if (typeof candidate.projectName !== 'string' || candidate.projectName.trim() === '') {
    throw new Error('Demo project manifest requires a projectName.');
  }
  if (!Array.isArray(candidate.tags) || candidate.tags.length === 0) {
    throw new Error('Demo project manifest requires at least one tag.');
  }
  if (!Array.isArray(candidate.codes) || candidate.codes.length === 0) {
    throw new Error('Demo project manifest requires at least one code pair.');
  }

  const tags = candidate.tags.map((tag) => {
    if (typeof tag !== 'string' || tag.trim() === '') {
      throw new Error('Every demo project tag must be a non-empty string.');
    }
    return tag.trim();
  });
  if (new Set(tags).size !== tags.length) {
    throw new Error('Demo project tags must be unique.');
  }

  const tagSet = new Set(tags);
  const codes = candidate.codes.map((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      throw new Error('Every demo project code must be a code/tag object.');
    }
    const pair = entry as { code?: unknown; tag?: unknown };
    if (typeof pair.code !== 'string' || pair.code.trim() === '') {
      throw new Error('Every demo project code must be a non-empty string.');
    }
    if (typeof pair.tag !== 'string' || !tagSet.has(pair.tag)) {
      throw new Error(`Demo code ${String(pair.code)} references an unknown tag.`);
    }
    return { code: pair.code.trim(), tag: pair.tag };
  });
  if (new Set(codes.map(({ code }) => code)).size !== codes.length) {
    throw new Error('Demo project codes must be unique.');
  }

  return Object.freeze({
    projectName: candidate.projectName.trim(),
    tags: Object.freeze(tags),
    codes: Object.freeze(codes),
  });
}

export const DEMO_PROJECT = validateManifest(manifest);
